import { FC, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { createColumnHelper } from '@tanstack/react-table';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import OrgDetailsModal from '@/components/modals/OrgDetailsModal/OrgDetailsModal.tsx';
import useAdminOrganizations, { type AdminOrg } from '@/api/admin/useAdminOrganizations.ts';

const columnHelper = createColumnHelper<AdminOrg>();

function formatJoined(dateStr: string | null) {
  if (!dateStr) return '—';
  return dayjs(dateStr).format('MMM D, YYYY');
}

const AdminOrganizationsPage: FC = () => {
  const { data: orgs = [], isLoading } = useAdminOrganizations();
  const [filter, setFilter] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<{ id: string; name: string } | null>(null);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => o.name.toLowerCase().includes(q));
  }, [orgs, filter]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => 'Operating Entity',
        cell: (info) => <span className="font-dm font-bold text-sm text-[#2b3440]">{info.getValue()}</span>,
      }),
      columnHelper.accessor('user_count', {
        header: () => 'Decision Participants',
        cell: (info) => <span className="font-dm text-sm text-[#2b3440]">{info.getValue()}</span>,
      }),
      columnHelper.accessor('decision_count', {
        header: () => 'Governance Events',
        cell: (info) => <span className="font-dm text-sm text-[#2b3440]">{info.getValue()}</span>,
      }),
      columnHelper.accessor('avg_governance_score', {
        header: () => 'Avg Score',
        cell: (info) => {
          const v = info.getValue();
          return <span className="font-dm text-sm text-[#2b3440]">{v !== null ? v.toFixed(1) : '—'}</span>;
        },
      }),
      columnHelper.accessor('pending_escalations', {
        header: () => 'Escalations',
        enableSorting: true,
        cell: (info) => {
          const v = info.getValue();
          return v > 0 ? (
            <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-red-100 text-red-600 font-dm font-bold text-[12px]">
              {v}
            </span>
          ) : (
            <span className="font-dm text-sm text-[#2b3440]">0</span>
          );
        },
      }),
      columnHelper.accessor('credits_remaining', {
        header: () => 'Exec. Decisions',
        cell: (info) => {
          const v = info.getValue();
          return (
            <span className={`font-dm text-sm ${v > 0 ? 'text-[#2b3440]' : 'text-[#2b3440]/40'}`}>
              {v ?? 0}
            </span>
          );
        },
      }),
      columnHelper.accessor('created_at', {
        header: () => 'Joined',
        cell: (info) => (
          <span className="font-dm text-sm text-[#2b3440] whitespace-nowrap">{formatJoined(info.getValue())}</span>
        ),
      }),
      columnHelper.display({
        id: 'action',
        header: () => 'Action',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            onClick={() => setSelectedOrg({ id: row.original.id, name: row.original.name })}
            className="font-dm font-bold text-sm text-[#0f1b2a] underline underline-offset-2 hover:opacity-70 transition-opacity whitespace-nowrap"
          >
            View details
          </button>
        ),
      }),
    ],
    []
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center px-6 py-4 bg-[#f4f9ff] rounded-xl">
        <div className="flex flex-col gap-1">
          <h1 className="font-newsreader text-[28px] font-extrabold leading-[125%] text-[rgba(0,0,0,0.87)]">
            Operating Entities
          </h1>
          <p className="font-dm text-[14px] font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
            View and monitor operating entity activity across COMMAND governed decision lifecycles.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <input
          type="text"
          placeholder="Filter by organization name…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="font-dm text-sm px-4 py-2 border border-[rgba(204,204,204,0.8)] rounded-lg w-full max-w-sm outline-none focus:border-[#1b3a6b] transition-colors"
        />

        {isLoading ? (
          <LoaderWrapper isLoading />
        ) : filtered.length === 0 ? (
          <p className="font-dm text-sm text-center text-[#2b3440]/50 py-10">No operating entities found.</p>
        ) : (
          <DataTable
            data={filtered}
            columns={columns}
            zebra
            className="table-auto"
            wrapperClassName="w-full rounded-2xl border border-base-300 bg-base-100"
          />
        )}
      </div>

      {selectedOrg && (
        <OrgDetailsModal
          orgId={selectedOrg.id}
          orgName={selectedOrg.name}
          isOpen
          onClose={() => setSelectedOrg(null)}
        />
      )}
    </div>
  );
};

export default AdminOrganizationsPage;
